import React, { useRef, useState } from 'react';
import { ActivityIndicator, Image, Pressable, StyleSheet, TextInput, View } from 'react-native';
import { Text } from './Texte';
import { Ionicons } from './icons';
import { OutlineIconButton } from './OutlineIconButton';
import { listerGardees, type OffreGardee } from '../services/offres';
import { ficheDepuisOffre } from '../utils/ficheDepuisOffre';
import { ouvrirAnnonce } from '../utils/ouvrirAnnonce';
import { sourceLibelle } from '../utils/offresAffichage';
import type { FicheDePoste } from '../hooks/useFicheDePoste';
import type { Offre } from '../types/offres';
import { capturesFichePossibles, MAX_FICHE_TEXTE } from '../services/mediaPicker';
import { currentLanguage, localeFormats, t } from '../i18n';
import { colors, radius, spacing, typography } from '../theme/tokens';

export interface FicheDePosteChampProps {
  fiche: FicheDePoste;
  /** Préfixe des testID : `cv-offer` garde ceux de la notation de CV. */
  prefixe: string;
  titre: string;
  aide: string;
  placeholder: string;
  libelleFichier: string;
  libelleCaptures: string;
  /** « Choisir une offre » parmi les offres gardées : seulement quand l'onglet Offres est actif. */
  avecOffresGardees?: boolean;
  /** Sources cumulées (« Optimiser mon CV ») : chacune retirable, texte compté et limité. */
  cumul?: boolean;
  /**
   * Refonte : le champ vit dans une section repliable qui porte déjà son titre et son cadre. Sans
   * cadre, sans titre, boutons de contour clair ; l'aide reste, c'est elle qui dit à quoi sert l'offre.
   */
  sansCadre?: boolean;
}

const milliers = (n: number) =>
  currentLanguage() === 'fr' ? String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : n.toLocaleString(localeFormats());

type Liste = { etat: 'fermee' } | { etat: 'chargement' } | { etat: 'erreur' } | { etat: 'ok'; offres: OffreGardee[] };

/**
 * Fiche de poste facultative : texte collé, PDF ou Word, captures, et, si l'onglet Offres est
 * actif, une offre sauvegardée. Partagée par la notation de CV et « Modifier mon CV ».
 *
 * Une offre Adzuna n'est qu'un extrait (500 caractères) : même avertissement qu'à l'écran 5, un
 * lien vers l'annonce complète et de quoi la coller à la place. L'avertissement tient tant que le
 * texte est l'extrait choisi, pas une fois remplacé.
 */
export function FicheDePosteChamp({
  fiche,
  prefixe,
  titre,
  aide,
  placeholder,
  libelleFichier,
  libelleCaptures,
  avecOffresGardees = false,
  cumul = false,
  sansCadre = false,
}: FicheDePosteChampProps) {
  const champ = useRef<TextInput>(null);
  const [liste, setListe] = useState<Liste>({ etat: 'fermee' });
  const [choisie, setChoisie] = useState<{ offre: Offre; texte: string } | null>(null);

  const ouvrirListe = async () => {
    setListe({ etat: 'chargement' });
    try {
      setListe({ etat: 'ok', offres: await listerGardees() });
    } catch {
      setListe({ etat: 'erreur' });
    }
  };

  const choisir = (offre: Offre) => {
    const texte = ficheDepuisOffre(offre);
    fiche.vider();
    fiche.setTexte(texte);
    setChoisie({ offre, texte });
    setListe({ etat: 'fermee' });
  };

  const collerComplete = () => {
    fiche.setTexte('');
    setChoisie(null);
    champ.current?.focus();
  };

  const extrait = choisie !== null && choisie.offre.source === 'adzuna' && fiche.texte === choisie.texte;
  // En cumul, les captures passent par le module de redimensionnement (binaire 1.1.0 et suivants).
  const avecCaptures = !cumul || capturesFichePossibles();

  return (
    <View style={sansCadre ? styles.sansCadre : styles.bloc}>
      {sansCadre ? null : <Text style={styles.titre}>{titre}</Text>}
      <Text style={styles.aide}>{aide}</Text>
      <TextInput
        ref={champ}
        testID={`${prefixe}-text`}
        style={[styles.saisie, sansCadre && styles.saisieClaire]}
        placeholder={placeholder}
        placeholderTextColor={colors.grey400}
        multiline
        value={fiche.texte}
        onChangeText={fiche.setTexte}
        maxLength={cumul ? MAX_FICHE_TEXTE : undefined}
      />
      {cumul ? (
        <Text style={styles.compteur}>{t('ficheDePoste.compteur', { n: milliers(fiche.texte.length), max: milliers(MAX_FICHE_TEXTE) })}</Text>
      ) : null}

      {extrait ? (
        <View testID={`${prefixe}-extrait`} style={styles.alerte}>
          <Text style={styles.alerteTexte}>{t('offres.relecture.extrait')}</Text>
          <View style={styles.liens}>
            {choisie.offre.urlOrigine ? (
              <Pressable accessibilityRole="button" onPress={() => void ouvrirAnnonce(choisie.offre.urlOrigine as string)} style={styles.lien}>
                <Text style={styles.lienTexte}>{t('offres.carte.voirOffreComplete')}</Text>
              </Pressable>
            ) : null}
            <Pressable accessibilityRole="button" onPress={collerComplete} style={styles.lien}>
              <Text style={styles.lienTexte}>{t('ficheDePoste.coller')}</Text>
            </Pressable>
          </View>
        </View>
      ) : null}

      <View style={styles.boutons}>
        <OutlineIconButton
          label={libelleFichier}
          icon={<Ionicons name="document-text" size={18} color={sansCadre ? colors.electricDeep : colors.electric} />}
          variante={sansCadre ? 'claire' : 'degrade'}
          onPress={() => void fiche.choisirFichier()}
          testID={`${prefixe}-pick`}
        />
        {avecCaptures ? (
          <OutlineIconButton
            label={libelleCaptures}
            icon={<Ionicons name="images" size={18} color={sansCadre ? colors.electricDeep : colors.electric} />}
            variante={sansCadre ? 'claire' : 'degrade'}
            onPress={() => void fiche.ajouterCaptures()}
            testID={`${prefixe}-images`}
          />
        ) : null}
      </View>

      {avecOffresGardees ? (
        <Pressable accessibilityRole="button" onPress={() => void ouvrirListe()} style={styles.lien} testID={`${prefixe}-choisir`}>
          <Text style={styles.lienTexte}>{t('ficheDePoste.choisirOffre')}</Text>
        </Pressable>
      ) : null}

      {liste.etat === 'chargement' ? (
        <View style={styles.ligne}>
          <ActivityIndicator color={colors.night} />
          <Text style={styles.aide}>{t('ficheDePoste.chargement')}</Text>
        </View>
      ) : null}
      {liste.etat === 'erreur' ? (
        <View style={styles.ligne}>
          <Text style={styles.erreur}>{t('ficheDePoste.erreur')}</Text>
          <Pressable accessibilityRole="button" onPress={() => void ouvrirListe()} style={styles.lien}>
            <Text style={styles.lienTexte}>{t('common.retry')}</Text>
          </Pressable>
        </View>
      ) : null}
      {liste.etat === 'ok' && liste.offres.length === 0 ? <Text style={styles.aide}>{t('ficheDePoste.vide')}</Text> : null}
      {liste.etat === 'ok' && liste.offres.length > 0 ? (
        <View style={styles.offres} testID={`${prefixe}-offres`}>
          {liste.offres.map(({ offre }) => (
            <Pressable key={offre.cle} accessibilityRole="button" onPress={() => choisir(offre)} style={styles.offre}>
              <Text style={styles.offreTitre} numberOfLines={2}>
                {offre.titre}
              </Text>
              <Text style={styles.aide} numberOfLines={1}>
                {[offre.entreprise.nom, sourceLibelle(offre.source, offre.partenaire)].filter(Boolean).join(' · ')}
              </Text>
            </Pressable>
          ))}
        </View>
      ) : null}

      {fiche.fichier ? (
        <View style={styles.ligne}>
          <Text testID={`${prefixe}-filename`} style={styles.fichier}>
            {fiche.fichier.name}
          </Text>
          {cumul ? (
            <Pressable accessibilityRole="button" accessibilityLabel={t('ficheDePoste.retirerFichier')} onPress={fiche.retirerFichier} style={styles.retirer} hitSlop={8}>
              <Ionicons name="close-circle" size={22} color={colors.grey500} />
            </Pressable>
          ) : null}
        </View>
      ) : null}
      {fiche.captures.length > 0 ? (
        <View testID={`${prefixe}-thumbs`} style={styles.vignettes}>
          {fiche.captures.map((img, i) => (
            <View key={`${img.uri}-${i}`}>
              <Image source={{ uri: img.uri }} style={styles.vignette} />
              {cumul ? (
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={t('ficheDePoste.retirerCapture', { n: i + 1 })}
                  onPress={() => fiche.retirerCapture(i)}
                  style={styles.retirerVignette}
                  hitSlop={8}
                >
                  <Ionicons name="close-circle" size={22} color={colors.night} />
                </Pressable>
              ) : null}
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  bloc: { gap: spacing.sm, padding: spacing.lg, borderRadius: radius.lg, backgroundColor: colors.white, borderWidth: 1, borderColor: colors.grey200 },
  sansCadre: { gap: spacing.sm },
  saisieClaire: { borderWidth: 1.5, borderRadius: radius.lg, backgroundColor: colors.grey50 },
  titre: {
    fontSize: typography.caption.size,
    fontWeight: typography.caption.weight,
    letterSpacing: typography.caption.letterSpacing,
    color: colors.grey500,
  },
  aide: { fontSize: typography.small.size, color: colors.grey500 },
  saisie: {
    minHeight: 88,
    borderWidth: 1,
    borderColor: colors.grey200,
    borderRadius: radius.md,
    padding: spacing.md,
    textAlignVertical: 'top',
    fontSize: typography.body.size,
    color: colors.grey900,
    backgroundColor: colors.white,
  },
  boutons: { flexDirection: 'row', gap: spacing.sm },
  alerte: { gap: spacing.xs, padding: spacing.md, borderRadius: radius.md, backgroundColor: colors.infoSoft },
  alerteTexte: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.night },
  liens: { flexDirection: 'row', flexWrap: 'wrap', columnGap: spacing.lg },
  lien: { minHeight: 44, justifyContent: 'center', alignSelf: 'flex-start' },
  lienTexte: { fontSize: typography.small.size, fontWeight: '700', color: colors.night, textDecorationLine: 'underline' },
  ligne: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap' },
  erreur: { color: colors.error, fontSize: typography.small.size },
  offres: { gap: spacing.xs },
  offre: { minHeight: 44, gap: 2, padding: spacing.md, borderRadius: radius.md, borderWidth: 1, borderColor: colors.grey200 },
  offreTitre: { fontSize: typography.bodyMd.size, fontWeight: '600', color: colors.night },
  fichier: { fontSize: typography.bodyMd.size, color: colors.bluePrimary },
  vignettes: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  vignette: { width: 56, height: 56, borderRadius: radius.sm, backgroundColor: colors.grey200 },
  retirer: { minWidth: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center' },
  retirerVignette: { position: 'absolute', top: -spacing.sm, end: -spacing.sm, backgroundColor: colors.white, borderRadius: radius.pill },
  compteur: { alignSelf: 'flex-end', fontSize: typography.small.size, color: colors.grey500 },
});
